import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import { parseSchedule, formatSchedule } from "../../components/SchedulePicker";
import s from "./TeachingGridView.module.css";

/** Mon–Fri "teaching grid" view of class offerings. Companion to the
 *  card list in ClassOfferingsPanel — same data, time-shaped lens. The
 *  head picks a teacher on the left and sees their week as colored
 *  subject blocks dropped into 7am–6pm hour rows. Click an empty cell
 *  to add; click a block to edit; conflicts (overlapping classes) get a
 *  red ring so the head can spot double-booking instantly. */

const DAYS = [
  { key: "Mon", label: "Monday" },
  { key: "Tue", label: "Tuesday" },
  { key: "Wed", label: "Wednesday" },
  { key: "Thu", label: "Thursday" },
  { key: "Fri", label: "Friday" },
];
const DAY_KEYS = DAYS.map(d => d.key);
const HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17];
/** Pixel height per hour row. Drives the subject block's vertical span:
 *  one hour ≈ 1 row, half-hour offsets land mid-row visually. */
const HOUR_PX = 56;

/** Eight-stop palette for subject blocks. Hashed by subject id so the
 *  same subject lands on the same color across renders. The colors are
 *  muted enough to coexist on one screen without fighting for attention,
 *  saturated enough that "Math always blue" recognition kicks in. */
const SUBJECT_PALETTE = [
  { bg: "linear-gradient(135deg,#fef3c7,#fde68a)", fg: "#7c4a03", accent: "#c9a227" },
  { bg: "linear-gradient(135deg,#dbeafe,#bfdbfe)", fg: "#1e3a8a", accent: "#2563eb" },
  { bg: "linear-gradient(135deg,#dcfce7,#bbf7d0)", fg: "#14532d", accent: "#16a34a" },
  { bg: "linear-gradient(135deg,#fce7f3,#fbcfe8)", fg: "#831843", accent: "#db2777" },
  { bg: "linear-gradient(135deg,#ede9fe,#ddd6fe)", fg: "#3b0764", accent: "#7c3aed" },
  { bg: "linear-gradient(135deg,#ffe4e6,#fecdd3)", fg: "#7f1d1d", accent: "#dc2626" },
  { bg: "linear-gradient(135deg,#e0f2fe,#bae6fd)", fg: "#0c4a6e", accent: "#0284c7" },
  { bg: "linear-gradient(135deg,#fef3e2,#fed7aa)", fg: "#7c2d12", accent: "#ea580c" },
];
function paletteFor(subjectId) {
  const id = Number(subjectId) || 0;
  return SUBJECT_PALETTE[Math.abs(id) % SUBJECT_PALETTE.length];
}

function getUser() {
  try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { return null; }
}
function authHeaders() {
  const u = getUser();
  return {
    "Content-Type": "application/json",
    "X-User-Role":  u?.role || "",
    "X-Username":   u?.username || "",
  };
}

function isTeacher(emp) {
  return emp && ["TEACHING", "TEACHER"].includes(emp.staffType);
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function toMinutes(t) {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  if (Number.isNaN(h)) return null;
  return h * 60 + (m || 0);
}

/** Explode each offering's "Mon/Wed 08:00-09:00" schedule into one
 *  block per (day, startMin, endMin). Blocks that don't fit the day-
 *  hour grid (e.g. 6am, 8pm) are still returned — the grid just clips
 *  them visually. Returns Map<dayKey, Array<block>>. */
function buildPlacements(offerings) {
  const byDay = Object.fromEntries(DAY_KEYS.map(k => [k, []]));
  for (const o of offerings) {
    const parsed = parseSchedule(o.schedule);
    const startMin = toMinutes(parsed.start);
    const endMin   = toMinutes(parsed.end);
    if (startMin == null || endMin == null) continue;
    for (const d of parsed.days) {
      if (!byDay[d]) continue;
      byDay[d].push({ offering: o, startMin, endMin });
    }
  }
  // Sort each day's blocks by start time so overlap detection is sequential.
  for (const k of DAY_KEYS) byDay[k].sort((a, b) => a.startMin - b.startMin);
  return byDay;
}

/** Mark blocks that overlap another block on the same day. The head
 *  needs to see double-booking at a glance — a red ring is louder than
 *  a footnote. Pure: returns a new Map of {block.offering.id → true}. */
function detectConflicts(placementsByDay) {
  const conflicting = new Set();
  for (const k of DAY_KEYS) {
    const day = placementsByDay[k];
    for (let i = 0; i < day.length; i++) {
      for (let j = i + 1; j < day.length; j++) {
        const a = day[i], b = day[j];
        // Half-open intervals — a class ending at 09:00 doesn't conflict
        // with one starting at 09:00.
        if (a.startMin < b.endMin && b.startMin < a.endMin) {
          conflicting.add(a.offering.id);
          conflicting.add(b.offering.id);
        }
      }
    }
  }
  return conflicting;
}

/** Pixel offset and height for a block, given the grid's 7am base and
 *  HOUR_PX per hour. Half-hour offsets are visually proportional. */
function blockGeom(startMin, endMin) {
  const baseMin = HOURS[0] * 60;            // 7:00
  const top    = ((startMin - baseMin) / 60) * HOUR_PX;
  const height = ((endMin - startMin) / 60) * HOUR_PX;
  return { top, height: Math.max(28, height) };
}

/** Friendly time label for a block. "08:00–09:00" is fine but compact
 *  is nicer when the block is short; we render this in the second line
 *  of each block so the head sees subject + time without clicking. */
function timeLabel(startMin, endMin) {
  function fmt(min) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    const period = h >= 12 ? "pm" : "am";
    const h12 = ((h + 11) % 12) + 1;
    return m === 0 ? `${h12}${period}` : `${h12}:${String(m).padStart(2, "0")}${period}`;
  }
  return `${fmt(startMin)}–${fmt(endMin)}`;
}

export default function TeachingGridView({
  semesterId,
  semesterLabel,
  employees,
  subjects,
  sections,
  offerings,
  onOfferingsChanged,
}) {
  const teachers = useMemo(
    () => employees.filter(isTeacher).sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [employees]
  );
  const subjectById = useMemo(() => Object.fromEntries(subjects.map(x => [x.id, x])), [subjects]);
  const sectionById = useMemo(() => Object.fromEntries(sections.map(x => [x.id, x])), [sections]);

  const [selectedTeacherId, setSelectedTeacherId] = useState(() => teachers[0]?.id ?? null);
  const [editing, setEditing] = useState(null);   // null | { offering } | { create: { day, startMin, endMin } }
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState("");

  // Re-pin the selected teacher when the list changes (e.g. switching
  // semesters that surface a different active staff list). Falls back
  // to the first teacher if the previous one disappears.
  useEffect(() => {
    if (selectedTeacherId == null) {
      if (teachers[0]) setSelectedTeacherId(teachers[0].id);
      return;
    }
    if (!teachers.some(t => t.id === selectedTeacherId) && teachers[0]) {
      setSelectedTeacherId(teachers[0].id);
    }
  }, [teachers, selectedTeacherId]);

  const selectedTeacher = useMemo(
    () => teachers.find(t => t.id === selectedTeacherId) || null,
    [teachers, selectedTeacherId]
  );

  // Offerings for this teacher only; filtered by semester at the parent
  // so we don't double-filter here.
  const teacherOfferings = useMemo(
    () => offerings.filter(o => o.teacherStaffId === selectedTeacherId),
    [offerings, selectedTeacherId]
  );

  const placements = useMemo(() => buildPlacements(teacherOfferings), [teacherOfferings]);
  const conflicts  = useMemo(() => detectConflicts(placements), [placements]);

  /** Workload total for each teacher's sidebar pill — sum of class
   *  durations across the week, in hours. Skips offerings without a
   *  parseable schedule so a malformed row doesn't poison the count. */
  const workloadByTeacher = useMemo(() => {
    const map = new Map();
    for (const o of offerings) {
      if (!o.teacherStaffId) continue;
      const parsed = parseSchedule(o.schedule);
      const startMin = toMinutes(parsed.start);
      const endMin   = toMinutes(parsed.end);
      if (startMin == null || endMin == null) continue;
      const dayCount = parsed.days.filter(d => DAY_KEYS.includes(d)).length;
      const hours = ((endMin - startMin) / 60) * dayCount;
      map.set(o.teacherStaffId, (map.get(o.teacherStaffId) || 0) + hours);
    }
    return map;
  }, [offerings]);

  /** Click an empty cell → seed an "add" form pre-filled with the day
   *  and a one-hour slot starting at the clicked hour. The user can
   *  edit days/time before saving — the click just gives a starting
   *  point so adding feels fluid. */
  function handleCellClick(dayKey, hour) {
    setErr("");
    setEditing({
      create: {
        day: dayKey,
        startMin: hour * 60,
        endMin: (hour + 1) * 60,
      },
    });
  }

  function handleBlockClick(offering) {
    setErr("");
    setEditing({ offering });
  }

  /** Persist the form. POST for new, PUT for existing. Re-pulls the
   *  parent's offerings via onOfferingsChanged so workload pills, the
   *  grid, and the card list outside this component all stay in sync. */
  async function handleSave(formValues) {
    if (!selectedTeacherId) return;
    setBusy(true); setErr("");
    try {
      const payload = {
        semesterId,
        teacherStaffId: selectedTeacherId,
        subjectId: Number(formValues.subjectId) || null,
        sectionId: Number(formValues.sectionId) || null,
        schedule:  formValues.schedule || "",
        room:      formValues.room     || "",
      };
      const url = editing.offering
        ? `${API_BASE}/api/class-offerings/${editing.offering.id}`
        : `${API_BASE}/api/class-offerings`;
      const res = await fetch(url, {
        method: editing.offering ? "PUT" : "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      setEditing(null);
      if (onOfferingsChanged) await onOfferingsChanged();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function handleDelete() {
    if (!editing?.offering) return;
    if (!window.confirm("Remove this class from the schedule?")) return;
    setBusy(true); setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/class-offerings/${editing.offering.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setEditing(null);
      if (onOfferingsChanged) await onOfferingsChanged();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  if (teachers.length === 0) {
    return (
      <div className={s.empty}>
        No teachers found. Add staff with type Teaching first under People → Staff.
      </div>
    );
  }

  return (
    <div className={s.wrap}>
      {/* Sidebar: teacher list. Workload pill shows weekly hours; helps
          the head spot overloaded vs available teachers at a glance. */}
      <aside className={s.sidebar}>
        <div className={s.sidebarHead}>
          <span className={s.sidebarLabel}>Teachers</span>
          <span className={s.sidebarCount}>{teachers.length}</span>
        </div>
        <div className={s.teacherList}>
          {teachers.map(t => {
            const hours = workloadByTeacher.get(t.id) || 0;
            const active = t.id === selectedTeacherId;
            return (
              <button
                key={t.id}
                type="button"
                className={`${s.teacherItem} ${active ? s.teacherItemActive : ""}`}
                onClick={() => setSelectedTeacherId(t.id)}>
                {t.photoUrl
                  ? <img src={t.photoUrl} alt="" className={s.avatarImg} />
                  : <div className={s.avatar}>{initials(t.name)}</div>
                }
                <div className={s.teacherName}>
                  <div className={s.teacherNameMain}>{t.name}</div>
                  <div className={s.teacherNameMeta}>
                    {hours > 0
                      ? `${hours.toFixed(hours % 1 === 0 ? 0 : 1)} hr/week`
                      : "No classes yet"}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* Main grid */}
      <section className={s.gridPane}>
        <header className={s.gridHead}>
          <div>
            <h3 className={s.gridTitle}>{selectedTeacher?.name || "—"}</h3>
            <p className={s.gridSub}>
              Mon–Fri schedule
              {semesterLabel ? <> · <span className={s.subAccent}>{semesterLabel}</span></> : null}
              {selectedTeacher?.role ? <> · {selectedTeacher.role}</> : null}
              <> · click an empty cell to add a class, click a block to edit</>
            </p>
          </div>
          {conflicts.size > 0 && (
            <div className={s.conflictBadge}>
              <span className={s.conflictDot} aria-hidden />
              {conflicts.size / 2} conflict{conflicts.size / 2 === 1 ? "" : "s"}
            </div>
          )}
        </header>

        {err && <div className={s.error}>{err}</div>}

        <div className={s.gridFrame}>
          {/* Sticky day headers above the scroll area */}
          <div className={s.gridHeaderRow}>
            <div className={s.timeGutterHead} />
            {DAYS.map(d => (
              <div key={d.key} className={s.dayHead}>{d.label}</div>
            ))}
          </div>

          {/* The grid body itself: a CSS grid where each cell is one
              hour × one day. Subject blocks are absolutely positioned
              over the day columns so a 90-min class can span two cells. */}
          <div className={s.gridBody}
               style={{ gridTemplateRows: `repeat(${HOURS.length}, ${HOUR_PX}px)` }}>
            {/* Time gutter — left column showing 7am, 8am, ... */}
            {HOURS.map((h, i) => (
              <div key={`time-${h}`}
                   className={s.timeCell}
                   style={{ gridRow: i + 1, gridColumn: 1 }}>
                {h === 12 ? "12pm" : (h > 12 ? `${h - 12}pm` : `${h}am`)}
              </div>
            ))}

            {/* Empty hour cells — clickable to add a class. One per
                (hour, day). Subject blocks paint on top via z-index. */}
            {DAYS.map((d, dayIdx) => HOURS.map((h, hourIdx) => (
              <button
                key={`cell-${d.key}-${h}`}
                type="button"
                className={s.cell}
                style={{ gridRow: hourIdx + 1, gridColumn: dayIdx + 2 }}
                onClick={() => handleCellClick(d.key, h)}
                aria-label={`Add class on ${d.label} ${h}:00`}
              />
            )))}

            {/* Subject blocks — absolutely positioned within each day
                column. Spans the full hour-grid via grid-row. */}
            {DAYS.map((d, dayIdx) => (
              <div
                key={`day-col-${d.key}`}
                className={s.dayColOverlay}
                style={{ gridColumn: dayIdx + 2, gridRow: `1 / ${HOURS.length + 1}` }}>
                {placements[d.key].map(block => {
                  const subj = subjectById[block.offering.subjectId];
                  const sec  = sectionById[block.offering.sectionId];
                  const pal  = paletteFor(block.offering.subjectId);
                  const { top, height } = blockGeom(block.startMin, block.endMin);
                  const isConflict = conflicts.has(block.offering.id);
                  return (
                    <button
                      key={`block-${d.key}-${block.offering.id}-${block.startMin}`}
                      type="button"
                      className={`${s.block} ${isConflict ? s.blockConflict : ""}`}
                      style={{
                        top,
                        height,
                        background: pal.bg,
                        color: pal.fg,
                        borderLeft: `4px solid ${pal.accent}`,
                      }}
                      onClick={(e) => { e.stopPropagation(); handleBlockClick(block.offering); }}
                      title={`${subj?.title || "?"} · ${timeLabel(block.startMin, block.endMin)}`}>
                      <div className={s.blockTitle}>
                        {subj?.code || "?"} <span className={s.blockTitleSep}>·</span>{" "}
                        <span className={s.blockTitleSub}>{subj?.title || ""}</span>
                      </div>
                      <div className={s.blockMeta}>
                        {sec?.name || "—"}
                        {block.offering.room ? <> · Rm {block.offering.room}</> : null}
                      </div>
                      <div className={s.blockTime}>{timeLabel(block.startMin, block.endMin)}</div>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </section>

      {editing && (
        <BlockEditor
          editing={editing}
          subjects={subjects}
          sections={sections}
          busy={busy}
          err={err}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

/** Compact editor for a single class block. Used both for adding a new
 *  class (when `editing.create` is set) and editing an existing one
 *  (when `editing.offering` is set). The schedule string is built and
 *  parsed via the same helpers as the rest of the app so the format
 *  stays canonical. */
function BlockEditor({ editing, subjects, sections, busy, err, onClose, onSave, onDelete }) {
  const isCreate = !!editing.create;
  const seedDays  = isCreate ? [editing.create.day] : (parseSchedule(editing.offering?.schedule).days || []);
  const seedStart = isCreate ? minutesToHHMM(editing.create.startMin) : (parseSchedule(editing.offering?.schedule).start || "");
  const seedEnd   = isCreate ? minutesToHHMM(editing.create.endMin)   : (parseSchedule(editing.offering?.schedule).end   || "");

  const [subjectId, setSubjectId] = useState(isCreate ? "" : (editing.offering?.subjectId ?? ""));
  const [sectionId, setSectionId] = useState(isCreate ? "" : (editing.offering?.sectionId ?? ""));
  const [days,  setDays]  = useState(seedDays);
  const [start, setStart] = useState(seedStart);
  const [end,   setEnd]   = useState(seedEnd);
  const [room,  setRoom]  = useState(isCreate ? "" : (editing.offering?.room ?? ""));

  // Escape closes the editor (outside-click no longer does), respecting
  // the same `busy` guard as the Cancel button.
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape" && !busy) onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  function toggleDay(k) {
    setDays(d => d.includes(k) ? d.filter(x => x !== k) : [...d, k].sort((a,b) => DAY_KEYS.indexOf(a) - DAY_KEYS.indexOf(b)));
  }

  const canSave = subjectId && sectionId && days.length > 0 && start && end && start < end;

  function submit(e) {
    e.preventDefault();
    if (!canSave) return;
    onSave({
      subjectId,
      sectionId,
      schedule: formatSchedule({ days, start, end }),
      room,
    });
  }

  return createPortal(
    <div className={s.modalOverlay}>
      <div className={s.modal} onClick={e => e.stopPropagation()}>
        <header className={s.modalHead}>
          <h3 className={s.modalTitle}>{isCreate ? "Add class to grid" : "Edit class"}</h3>
          <button type="button" className={s.modalClose} onClick={onClose}>×</button>
        </header>
        <form className={s.modalBody} onSubmit={submit}>
          <label className={s.field}>
            <span>Subject</span>
            <select value={subjectId} onChange={e => setSubjectId(e.target.value)} required>
              <option value="">— pick a subject —</option>
              {subjects.slice().sort((a,b) => (a.code || "").localeCompare(b.code || "")).map(x => (
                <option key={x.id} value={x.id}>{x.code} · {x.title}</option>
              ))}
            </select>
          </label>

          <label className={s.field}>
            <span>Section</span>
            <select value={sectionId} onChange={e => setSectionId(e.target.value)} required>
              <option value="">— pick a section —</option>
              {sections.slice().sort((a,b) => (a.name || "").localeCompare(b.name || "")).map(x => (
                <option key={x.id} value={x.id}>{x.name}</option>
              ))}
            </select>
          </label>

          <div className={s.field}>
            <span>Days</span>
            <div className={s.dayRow}>
              {DAYS.map(d => (
                <button
                  key={d.key}
                  type="button"
                  className={`${s.dayBtn} ${days.includes(d.key) ? s.dayBtnActive : ""}`}
                  onClick={() => toggleDay(d.key)}
                  title={d.label}>
                  {d.key}
                </button>
              ))}
            </div>
          </div>

          <div className={s.fieldRow}>
            <label className={s.field}>
              <span>Start</span>
              <input type="time" value={start} onChange={e => setStart(e.target.value)} step="300" />
            </label>
            <label className={s.field}>
              <span>End</span>
              <input type="time" value={end} onChange={e => setEnd(e.target.value)} step="300" />
            </label>
          </div>

          <label className={s.field}>
            <span>Room (optional)</span>
            <input value={room} onChange={e => setRoom(e.target.value)} placeholder="e.g. 201" />
          </label>

          {err && <div className={s.error}>{err}</div>}

          <div className={s.modalFoot}>
            {!isCreate && (
              <button type="button" className={s.deleteBtn} onClick={onDelete} disabled={busy}>
                Delete
              </button>
            )}
            <span className={s.spacer} />
            <button type="button" className={s.cancelBtn} onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className={s.saveBtn} disabled={!canSave || busy}>
              {busy ? "Saving…" : (isCreate ? "Add class" : "Save changes")}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function minutesToHHMM(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
